import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Gift, Share2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pill } from "@/components/Badges";
import { api, errMsg, fmtDate } from "@/lib/api";

const STATUS = { pending: ["amber", "Waiting for approval"], approved: ["sky", "Joined"], rewarded: ["emerald", "Reward earned"] };

function FriendsList({ friends }) {
  if (!friends.length) return <p className="text-sm text-muted-foreground" data-testid="refer-no-friends">No friends have joined with your link yet.</p>;
  return (
    <div className="divide-y divide-border/60" data-testid="refer-friends-list">
      {friends.map((f, i) => (
        <div key={i} className="flex items-center justify-between py-3">
          <div><p className="text-sm font-medium">{f.name}</p><p className="font-mono-data text-xs text-muted-foreground">Joined {fmtDate(f.joined)}</p></div>
          <Pill color={STATUS[f.status][0]}>{STATUS[f.status][1]}</Pill>
        </div>
      ))}
    </div>
  );
}

export default function Refer() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/my/referrals").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!d) return null;
  const link = `${window.location.origin}/register?ref=${d.code}`;
  const available = d.rewards.filter((r) => r.status === "available");
  const copy = async () => {
    await navigator.clipboard.writeText(link);
    toast.success("Link copied");
  };
  const share = () => navigator.share({ title: "Korevex Bio", text: `Join me on Korevex Bio and get ${d.friend_percent}% off your first order`, url: link }).catch(() => {});

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:py-14">
      <p className="eyebrow mb-3">Rewards</p>
      <h1 className="text-4xl font-bold sm:text-5xl">Refer a friend</h1>
      {!d.enabled ? (
        <p className="mt-6 rounded-lg border border-border bg-card p-6 text-muted-foreground" data-testid="refer-paused">The referral program is paused right now. Check back soon.</p>
      ) : (
        <>
          <p className="mt-3 max-w-xl text-muted-foreground" data-testid="refer-how-it-works">
            Your friend gets <span className="text-emerald-400">{d.friend_percent}% off</span> their first order. Once their first order is paid, you get <span className="text-emerald-400">{d.referrer_percent}% off</span> your next one.
          </p>
          <div className="mt-8 rounded-lg border border-border border-t-2 border-t-emerald-500 bg-card p-6">
            <p className="mb-2 text-sm font-medium">Your referral link</p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input readOnly value={link} className="font-mono-data text-xs" data-testid="refer-link-input" />
              <Button onClick={copy} className="gap-2" data-testid="refer-copy-button"><Copy className="h-4 w-4" />Copy</Button>
              {navigator.share && <Button variant="outline" onClick={share} className="gap-2" data-testid="refer-share-button"><Share2 className="h-4 w-4" />Share</Button>}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">Friends who sign up with your link are approved by Korevex Bio before they can order.</p>
          </div>
        </>
      )}
      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-6">
          <p className="mb-4 flex items-center gap-2 font-medium"><Gift className="h-4 w-4 text-emerald-400" />Your rewards</p>
          <p className="font-mono-data text-3xl text-emerald-400" data-testid="refer-rewards-available">{available.length}</p>
          <p className="text-sm text-muted-foreground">{available.length ? `Ready to use. ${available[0].percent}% comes off your next order automatically.` : "No rewards waiting yet."}</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-6">
          <p className="mb-2 flex items-center gap-2 font-medium"><Users className="h-4 w-4 text-sky-400" />Friends you referred</p>
          <FriendsList friends={d.friends} />
        </div>
      </div>
    </div>
  );
}
